import React from 'react';
import { Card, Typography, Box, Chip, Stack } from '@mui/material';
import IconPopoverMenu from '../IconPopoverMenu/IconPopoverMenu';
import EditIcon from '../CustomSVGIcons/EditIcon';
import { theme } from '../../constants/theme';
import { useThemeContext } from '../../hook/useThemeManager';
import { Instagram, Youtube, Facebook, Music, X } from 'lucide-react';
import { FONT_ENUM } from '../../types/global/fonts.types';
import { formatSocialHandle } from '../../utils/socialHandle';
import ThspianIcon from '../CustomSVGIcons/ThspianIcon';

export interface ISocialMediaData {
    platform: string;
    username: string;
    followers?: string | number;
    following?: string | number;
    engagement?: string | number;
    industry?: string[];
    bio?: string;
}

export interface ISocialMediaCardProps {
    data: ISocialMediaData;
    onDelete?: () => void;
    onDuplicate?: () => void;
    onEdit?: () => void;
}

const getPlatformIcon = (platform: string) => {
    switch (platform.toLowerCase()) {
        case 'instagram':
            return Instagram;
        case 'youtube':
            return Youtube;
        case 'facebook':
            return Facebook;
        case 'twitter':
            return X;
        case 'tiktok':
            return Music;
        case 'thspian':
            return ThspianIcon; // Default icon for thspian
        default:
            return Instagram;
    }
};

const getPlatformColor = (platform: string) => {
    switch (platform.toLowerCase()) {
        case 'instagram':
            return '#E4405F';
        case 'youtube':
            return '#FF0000';
        case 'facebook':
            return '#1877F2';
        case 'twitter':
            return '#1DA1F2';
        case 'tiktok':
            return '#000000';
        case 'thspian':
            return '#E38627'; // Indigo color for thspian
        default:
            return '#E4405F';
    }
};

const formatNumber = (num: string | number | undefined) => {
    if (!num) return '0';
    const numValue = typeof num === 'string' ? parseFloat(num) : num;
    if (numValue >= 1000000) {
        return (numValue / 1000000).toFixed(1) + 'M';
    } else if (numValue >= 1000) {
        return (numValue / 1000).toFixed(1) + 'K';
    }
    return numValue.toString();
};

const SocialMediaCard: React.FC<ISocialMediaCardProps> = ({ data, onDelete, onDuplicate, onEdit }) => {
    const { themeStyle } = useThemeContext();
    const IconComponent = getPlatformIcon(data.platform);
    const platformColor = getPlatformColor(data.platform);

    const handleMenuAction = (action: 'edit' | 'delete' | 'duplicate') => {
        switch (action) {
            case 'delete':
                onDelete && onDelete();
                break;
            case 'duplicate':
                onDuplicate && onDuplicate();
                break;
            case 'edit':
                onEdit && onEdit();
                break;
        }
    };

    return (
        <Card
            sx={{
                display: 'flex',
                flexDirection: 'column',
                p: 2,
                maxWidth: '100%',
                position: 'relative',
                my: 1,
                backgroundColor: theme[themeStyle].secondaryBg,
                border: `1px solid ${theme[themeStyle].tag_white}`,
                borderRadius: 2,
                boxShadow: `0px 2px 4px ${theme[themeStyle].shadowColor}`,
                '&:hover': {
                    boxShadow: `0px 4px 8px ${theme[themeStyle].shadowColor}`,
                    border: `1px solid ${theme[themeStyle].highlight}`,
                },
                transition: 'box-shadow 0.2s, border 0.2s',
            }}>
            {/* Header with Platform Icon and Username */}
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <IconComponent size={24} style={{ color: platformColor, marginRight: 12, stroke: platformColor }} />
                <Box sx={{ flexGrow: 1 }}>
                    <Typography
                        variant="h6"
                        sx={{
                            color: theme[themeStyle].tag_white,
                            fontFamily: FONT_ENUM.MAIN_BOLD,
                            fontWeight: 600,
                            textTransform: 'capitalize',
                            fontSize: 12,
                        }}>
                        {data.platform}
                    </Typography>
                    <Typography
                        variant="body2"
                        sx={{
                            color: theme[themeStyle].light_gray_text,
                            fontFamily: FONT_ENUM.MAIN_REGULAR,
                        }}>
                        {formatSocialHandle(data.username)}
                    </Typography>
                </Box>
                <Box sx={{ position: 'absolute', top: 8, right: 8 }}>
                    <IconPopoverMenu
                        icon={<EditIcon stroke={theme[themeStyle].tag_white} height={'24px'} width={'24px'} />}
                        onOptionClick={handleMenuAction}
                        iconButtonStyle={{
                            color: theme[themeStyle].tag_white,
                        }}
                    />
                </Box>
            </Box>

            {/* Stats Row */}
            <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
                {data.followers && (
                    <Box>
                        <Typography
                            variant="body2"
                            sx={{
                                color: theme[themeStyle].tag_white,
                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                fontWeight: 600,
                            }}>
                            {formatNumber(data.followers)}
                        </Typography>
                        <Typography
                            variant="caption"
                            sx={{
                                color: theme[themeStyle].light_gray_text,
                                fontFamily: FONT_ENUM.MAIN_REGULAR,
                            }}>
                            Followers
                        </Typography>
                    </Box>
                )}
                {data.following && (
                    <Box>
                        <Typography
                            variant="body2"
                            sx={{
                                color: theme[themeStyle].tag_white,
                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                fontWeight: 600,
                            }}>
                            {formatNumber(data.following)}
                        </Typography>
                        <Typography
                            variant="caption"
                            sx={{
                                color: theme[themeStyle].light_gray_text,
                                fontFamily: FONT_ENUM.MAIN_REGULAR,
                            }}>
                            Following
                        </Typography>
                    </Box>
                )}
                {data.engagement && (
                    <Box>
                        <Typography
                            variant="body2"
                            sx={{
                                color: theme[themeStyle].tag_white,
                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                fontWeight: 600,
                            }}>
                            {data.engagement}%
                        </Typography>
                        <Typography
                            variant="caption"
                            sx={{
                                color: theme[themeStyle].light_gray_text,
                                fontFamily: FONT_ENUM.MAIN_REGULAR,
                            }}>
                            Engagement
                        </Typography>
                    </Box>
                )}
            </Stack>

            {/* Industry Tags */}
            {data.industry && data.industry.length > 0 && (
                <Box sx={{ mb: 2 }}>
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                        {data.industry.slice(0, 3).map((industry, index) => (
                            <Chip
                                key={index}
                                label={industry}
                                size="small"
                                sx={{
                                    backgroundColor: theme[themeStyle].deep_grey,
                                    color: theme[themeStyle].tag_white,
                                    fontFamily: FONT_ENUM.MAIN_REGULAR,
                                    fontSize: '0.75rem',
                                }}
                            />
                        ))}
                    </Stack>
                </Box>
            )}

            {/* Bio */}
            {data.bio && (
                <Typography
                    variant="body2"
                    sx={{
                        color: theme[themeStyle].light_gray_text,
                        fontFamily: FONT_ENUM.MAIN_REGULAR,
                        fontSize: '0.875rem',
                        lineHeight: 1.4,
                    }}>
                    {data.bio}
                </Typography>
            )}
        </Card>
    );
};

export { SocialMediaCard };
